import React, { useState } from 'react';
import { Database, Upload, RefreshCw } from 'lucide-react';
import { Button, toast } from '@ncoder/ui';
import { TrainingDataContent } from '@/components/organisms/training/TrainingDataContent';

interface Dataset {
  id: number;
  name: string;
  size: string;
  records: number;
  status: 'processed' | 'processing' | 'pending';
  quality: number;
}

export const TrainingData: React.FC = () => {
  const [datasets] = useState<Dataset[]>([
    {
      id: 1,
      name: 'Customer Support Conversations',
      size: '15.2 MB',
      records: 3247,
      status: 'processed',
      quality: 94,
    },
    {
      id: 2,
      name: 'Product Knowledge Base',
      size: '8.7 MB',
      records: 1856,
      status: 'processing',
      quality: 89,
    },
    {
      id: 3,
      name: 'FAQ Dataset',
      size: '2.1 MB',
      records: 567,
      status: 'processed',
      quality: 97,
    },
  ]);

  const [trainingProgress] = useState(75);

  const totalRecords = datasets.reduce((sum, ds) => sum + ds.records, 0);
  const overallQuality = Math.round(
    datasets.reduce((sum, ds) => sum + ds.quality, 0) / datasets.length
  );

  const handleRetrain = () => {
    toast({
      title: 'Retraining Started',
      description: 'Your agent is being retrained with the latest data.',
    });
  };

  const handleUploadData = () => {
    toast({
      title: 'Upload Data',
      description: 'Opening file upload dialog...',
    });
  };

  const handleViewDataset = (id: number) => {
    toast({
      title: 'View Dataset',
      description: `Opening dataset ${id} details...`,
    });
  };

  const handleEditDataset = (id: number) => {
    toast({
      title: 'Edit Dataset',
      description: `Editing dataset ${id}...`,
    });
  };

  const handleRunValidation = () => {
    toast({
      title: 'Running Validation',
      description: 'Validation test started. This may take a few minutes.',
    });
  };

  const handleViewAnalytics = () => {
    toast({
      title: 'Analytics',
      description: 'Opening analytics dashboard...',
    });
  };

  const handleImportDataset = () => {
    toast({
      title: 'Import Dataset',
      description: 'Select a dataset to import...',
    });
  };

  const handleStartTraining = () => {
    toast({
      title: 'Training Started',
      description: 'Your agent training has begun.',
    });
  };

  return (
    <div className="w-full max-w-none space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-page-title flex items-center gap-2">
            <Database className="h-8 w-8 text-primary" />
            Training Data
          </h1>
          <p className="text-subtitle">Manage and optimize your agent's training datasets</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRetrain}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retrain
          </Button>
          <Button size="sm" onClick={handleUploadData}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Data
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <TrainingDataContent
        datasets={datasets}
        onViewDataset={handleViewDataset}
        onEditDataset={handleEditDataset}
        overallQuality={overallQuality}
        totalRecords={totalRecords}
        trainingProgress={trainingProgress}
        epochsCompleted={7}
        totalEpochs={10}
        timeRemaining="2h 15m"
        accuracy="89%"
        precision="92%"
        recall="87%"
        onRunValidation={handleRunValidation}
        currentModel="v2.1"
        lastTrained="2 hours ago"
        trainingTime="4h 32m"
        responseQuality="94%"
        contextUnderstanding="91%"
        taskCompletion="88%"
        onViewAnalytics={handleViewAnalytics}
        onImportDataset={handleImportDataset}
        onStartTraining={handleStartTraining}
      />
    </div>
  );
};

export default TrainingData;